import { Editor } from "@tinymce/tinymce-react";
import Loader from "components/loader";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";
import crossIcon from "../../assets/svg/crossBtn.svg";
import uploadIcon from "../../assets/svg/upload-icon.svg";
import {
  addFlMessages,
  getFlMessageById,
  updateFlMessage,
} from "../../axios/api";
import { renderError, renderImagePreview } from "../../utils/utils";

const AddFlMessage = () => {
  const editorRef = useRef(null);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location?.state;

  const [FlData, setFlData] = useState<any>();
  const [editorContent, setEditorContent] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const styleForEditor =
    ':root { --background-color: #fff; --boxshadow-color: #00000030; } body { font-family: Poppins, sans-serif; } .dark { --background-color: #1F2937; --boxshadow-color: #BDBDBD75; color: #fff; } details > summary::-webkit-details-marker { display: none; } summary { background-color: var(--background-color); padding: 10px; cursor: pointer; margin-right: 0.5rem; } details summary { list-style: none; cursor: pointer; position: relative; } details p { padding: 0.5rem; } details summary::marker { display: none; } details summary::before { content: "▼"; display: inline-block; width: 1em; height: 1em; position: absolute; top: 20%; right: 5%; transition: all 0.3s ease; } details[open] summary::before { transform: rotate(180deg); top: 40%; } details { width: 92%; margin-left: calc(4% - 6px); box-shadow: 0 0 10px var(--boxshadow-color); padding: 5px; background-color: var(--background-color); cursor: pointer; margin-bottom: 15px; }';
  const fileInputRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      description: "",
      image: null,
      ImageUrl: "",
    },
    onSubmit: async (values) => {
      if (!values.description && !values.image && !values.ImageUrl) {
        toast.error("Please add at least an image or description");
        return;
      }

      try {
        setLoading(true);
        const formData = new FormData();
        const fullContent = `<html><head><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap" rel="stylesheet"><meta name="viewport" content="width=device-width, initial-scale=1.0"><meta http-equiv="Content-Security-Policy" content="default-src *; img-src * 'self' data:; script-src 'unsafe-inline' 'unsafe-eval' *; style-src 'unsafe-inline' *;"><style>${styleForEditor}</style></head><body>${editorContent}</body></html>`;
        formData.append("locale", "1");
        if (editorContent) {
          formData.append("description", fullContent);
        } else {
          formData.append("description", "");
        }
        formData.append(
          "image",
          (values?.image && values.image) ||
            (values?.ImageUrl &&
              values.ImageUrl.replace(
                /(https?:\/\/)?(?:\d{1,3}\.){3}\d{1,3}/,
                ""
              )) ||
            ""
        );
        if (!FlData) {
          formData.append("financial_message_id", uuidv4());
        }

        let responseData: any;
        if (id) {
          responseData = await updateFlMessage(formData, id);

          if (responseData?.data?.success) {
            toast.success("FL Message Updated Successfully");
            navigate("/admin/fl-lt-message");
          } else {
            toast.error(responseData?.data?.message);
          }
        } else {
          responseData = await addFlMessages(formData);
          if (responseData?.data?.success) {
            toast.success("FL Message Added Successfully");
            navigate("/admin/fl-lt-message");
          } else {
            toast.error(responseData?.data?.message);
          }
        }
        if (!responseData?.data?.success) {
          toast.error(responseData?.response?.data?.message);
        }
      } catch (error: any) {
        if ((error.response.data.message = "File too large")) {
          toast.error("Image size should be less then 100kb");
        } else {
          toast.error("Something Went Wrong" + error.message);
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const checkImage = useMemo(
    () => renderImagePreview(formik.values.image, formik.values.ImageUrl),
    [formik.values.image, formik.values.ImageUrl]
  );

  // Trigger fileInput on click on div
  const handleDivClick = (ref: any) => {
    ref.current.click();
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
  };

  const handleDrop = (e: any, fieldName: any) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file.size > 100 * 1024) {
      setPopupMessage("Image size should be less than 100kb.");
      formik.setFieldValue(fieldName, null);
    } else {
      setPopupMessage("");
      formik.setFieldValue(fieldName, file);
    }
  };

  const handleEditorChange = (content: any, editor: any) => {
    setEditorContent(content);
    formik.setFieldValue("description", content);
  };

  useEffect(() => {
    const fetchFlMessage = async (id: string) => {
      try {
        setLoading(true);
        const announcementDataResp = await getFlMessageById(id);
        setFlData(announcementDataResp?.data?.data);
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchFlMessage(id);
    }
  }, []);

  useEffect(() => {
    if (FlData) {
      formik.setValues({
        description: FlData.description,
        image: null,
        ImageUrl: FlData.image,
      });
      setEditorContent(FlData.description);
    }
  }, [FlData]);

  return (
    <div className="m-3 bg-[#FFFFFF] p-3">
      {loading ? (
        <Loader />
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="my-5 flex w-11/12 flex-col">
            <div className="my-5  gap-5">
              <div className="w-[35%]">
                <div className="mb-1 flex flex-col">
                  <label htmlFor="ImageUrl" className=" text-xl font-medium">
                    Financial Literacy Image
                  </label>
                  <span className=" text-sm text-red-500">
                    (Image resolution should be 350*150)
                  </span>
                  <span className=" text-sm text-red-500">
                    (Valid Image Formats: jpg, jpeg, png and gif)
                  </span>
                </div>

                <div className="relative my-3 mb-5 flex w-[100%] items-center justify-between">
                  <input
                    // className="absolute bottom-0 left-8 -z-10 w-20"
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    // required={!formik.values.ImageUrl && !formik.values.image}
                    name="ImageUrl"
                    id="ImageUrl"
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg, .jpeg, .png, .gif"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file?.size > 100 * 1024) {
                        setPopupMessage(
                          "Image size should be less than 100kb."
                        );
                        formik.setFieldValue("ImageUrl", "");
                      } else {
                        setPopupMessage("");
                        formik.setFieldValue("ImageUrl", file?.name);
                        formik.setFieldValue("image", file);
                      }
                    }}
                  />
                  <div
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, "image")}
                    onClick={() => handleDivClick(fileInputRef)}
                    className="relative flex h-44 w-full items-center justify-center overflow-hidden rounded border-4 border-dashed"
                  >
                    {formik.values.ImageUrl || formik.values.image ? (
                      <>
                        <div className="relative flex items-center p-2">
                          {(formik.values.image || formik.values.ImageUrl) && (
                            <img
                              className="object-scale-down"
                              alt="banner-img"
                              src={checkImage}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation(); // Stop event propagation
                            formik.setFieldValue("image", "");
                            formik.setFieldValue("ImageUrl", "");
                            fileInputRef.current.value = null;
                          }}
                          className="absolute right-1 top-1 z-10"
                        >
                          <img src={crossIcon} alt="Cross Icon" />
                        </button>
                      </>
                    ) : (
                      <div
                        id="imageDiv"
                        className="flex flex-col items-center justify-center text-center font-medium hover:cursor-pointer"
                      >
                        <img
                          className="mb-2 w-1/4"
                          src={uploadIcon}
                          alt="Upload Icon"
                        />
                        <span>
                          <span className="text-blue-500">Click here </span>to
                          Upload
                        </span>
                        <span> Banner Image</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {popupMessage && (
                <div className=" top-full mt-2 text-red-500">
                  {popupMessage}
                </div>
              )}
            </div>
            <div className="my-5">
              <label htmlFor="" className="my-2 text-xl font-medium">
                FL Message
              </label>
              <div className="my-3">
                <Editor
                  onEditorChange={handleEditorChange}
                  tinymceScriptSrc="/tinymce/tinymce.min.js"
                  licenseKey="gpl"
                  onInit={(_evt, editor) => (editorRef.current = editor)}
                  value={editorContent}
                  init={{
                    relative_urls: false,
                    remove_script_host: false,
                    convert_urls: true,
                    height: 500,
                    menubar: false,
                    plugins: [
                      "advlist",
                      "autolink",
                      "lists",
                      "link",
                      "preview",
                      "anchor",
                      "visualblocks",
                      "code",
                      "fullscreen",
                      "insertdatetime",
                      "media",
                      "help",
                      "wordcount",
                      "accordion",
                      "preview",
                      "emoticons",
                      "link", // Custom plugin should be correctly referenced
                    ],
                    toolbar: [
                      "undo redo | blocks blockquote | " +
                        "bold italic underline strikethrough superscript subscript | forecolor backcolor | " +
                        "align bullist numlist | outdent indent | " +
                        "emoticons | removeformat | preview | help", // Ensure toolbar buttons are added
                    ],
                    toolbar_sticky: true,
                    content_style: styleForEditor,
                    // setup: (editor) => {
                    //   editor.on("change", (e) => {
                    //     handleEditorChange(
                    //       editor.getContent({ format: "html" }),
                    //       editor
                    //     );
                    //   });
                    // },
                  }}
                />
              </div>
              {formik.touched.description && formik.errors.description ? (
                <div>{renderError(formik.errors.description)}</div>
              ) : null}
            </div>
            <div>
              <button
                name="submit"
                className="float-right my-5 w-24 rounded bg-[#008BD2] p-2 text-xl font-bold text-white"
                type="submit"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default AddFlMessage;
